import { config } from '../config.ts';
import { DataError } from '../domain/codec.ts';
import type { ActionKind, Side, SlotKind, LineColor } from '../domain/types.ts';
import { simService, store } from '../state/instance.ts';
import { addNode, copyNode, addTab, selectTab, startRenameTab, tabMenu, createPlan, createTeam, deleteNode, deletePlan, deleteTeam, duplicatePlan, editPaste, loadImportFile, pickSlot, renameTeam, showExport, showImport } from './actions.ts';
import { accountMenu, openSignIn, signInWith } from './account.ts';
import { openPalettePicker } from './theme.ts';
import { requestRender } from './bus.ts';
import { copyShareLink, openShareDialog, redrawSharedLinks, stopSharingPlan } from './share.ts';
import { session } from '../state/account.ts';
import { qs } from './dom.ts';
import { setOutcome } from './outcome-edit.ts';
import { openReplayImport } from './replay-import.ts';
import { openMenu, toast } from './overlays.ts';
import { isPivotMove, renderDrawer } from './views/drawer.ts';
import { rerenderCanvas, revealTurn, setZoom } from './views/plan-view.ts';
import { followTo } from './views/mobile-plan.ts';
import { isMobile } from './mobile.ts';
import { COFFEE_URL, FEEDBACK_URL, GITHUB_URL, X_URL } from './ext-links.ts';
import { applyNavCollapsed, renderNav } from './views/sidebar.ts';
import { LINE_COLORS } from '../domain/types.ts';
import { cleanTags } from '../domain/codec.ts';

const asSide = (v: string | undefined): Side => (v === 'opp' ? 'opp' : 'me');
const asSlot = (v: string | undefined): SlotKind => (v === 'back' ? 'back' : 'lead');
const asKind = (v: string): ActionKind => (v === 'switch' || v === 'mega' ? v : 'move');

export function reportError(error: unknown): void {
  console.error(error);
  toast(error instanceof DataError ? error.message : 'Something went wrong. Your data is still saved; try again or reload the page.', true);
}

function selectTeam(teamId: string, planId: string | null): void {
  Object.assign(store.ui, { teamId, planId, tabId: null, selectedNode: null });
  store.ui.open[teamId] = true;
  store.persist();
  requestRender();
}

async function handleClick(el: HTMLElement, e: MouseEvent): Promise<void> {
  const d = el.dataset;
  const team = d.team ?? '';
  const plan = d.plan ?? '';
  // Phones: picking something in the team menu closes it.
  if (store.ui.navOpen && NAV_CLOSERS.has(d.act ?? '')) { store.ui.navOpen = false; applyNavCollapsed(); }
  switch (d.act) {
    case 'open-nav': store.ui.navOpen = true; applyNavCollapsed(); qs<HTMLElement>('#nav')?.focus(); return;
    case 'close-nav': store.ui.navOpen = false; applyNavCollapsed(); return;
    case 'plan-more': {
      e.stopPropagation();
      const link = (url: string): (() => void) => () => { window.open(url, '_blank', 'noopener,noreferrer'); };
      openMenu(el, [
        { label: 'Add branch from replay', run: () => { void openReplayImport(); } },
        { label: 'Export gameplan', run: () => void showExport({ type: 'plan', teamId: team, planId: plan }) },
        '-',
        { label: 'Send feedback', run: link(FEEDBACK_URL) },
        { label: 'Buy me a coffee', run: link(COFFEE_URL) },
        { label: 'Source code on GitHub', run: link(GITHUB_URL) },
        { label: '@PeteVGC_ on X', run: link(X_URL) },
      ]);
      return;
    }
    case 'm-setup':
      // The section opens or closes itself; remember it, so redrawing the plan leaves it as it was.
      store.ui.setupOpen = !(el.closest('details')?.open ?? false);
      return;
    case 'm-view':
      store.ui.mobileView = d.view === 'tree' ? 'tree' : 'path';
      rerenderCanvas();
      qs('#canvas')?.scrollTo({ top: 0 });
      return;
    case 'm-pick':
      if (d.key && d.node) store.ui.pathPick[d.key] = d.node;
      rerenderCanvas();
      return;
    case 'm-fold':
      if (d.node) store.ui.folded[d.node] = !store.ui.folded[d.node];
      rerenderCanvas();
      return;
    case 'm-goto': {
      const sheet = store.sheet;
      if (!sheet || !d.node) return;
      followTo(sheet, d.node);
      store.ui.mobileView = 'path';
      rerenderCanvas();
      revealTurn(d.node);
      return;
    }
    case 'new-team': return createTeam();
    case 'import': if (el.tagName === 'A') e.preventDefault(); return showImport();
    case 'export-all':
      if (store.library.teams.length) return showExport({ type: 'all' });
      toast('Nothing to export yet', true);
      return;
    case 'sign-in': return openSignIn();
    case 'sign-in-with': signInWith(d.provider ?? ''); return;
    case 'account-menu': e.stopPropagation(); accountMenu(el); return;
    case 'theme': return openPalettePicker();
    case 'toggle-nav':
      store.ui.navCollapsed = !store.ui.navCollapsed;
      store.persist();
      applyNavCollapsed();
      return;
    case 'toggle-team':
      e.stopPropagation();
      store.ui.open[team] = !store.ui.open[team];
      store.persist();
      renderNav();
      return;
    case 'open-team': selectTeam(team, null); return;
    case 'open-plan':
      e.stopPropagation();
      Object.assign(store.ui, { scrollX: 0, scrollY: 0 });
      selectTeam(team, plan);
      return;
    case 'team-menu':
      e.stopPropagation();
      openMenu(el, [
        { label: 'Rename', run: () => void renameTeam(team) },
        { label: 'New gameplan', run: () => void createPlan(team) },
        { label: 'Export team', run: () => void showExport({ type: 'team', teamId: team }) },
        '-',
        { label: 'Delete', cls: 'danger', run: () => void deleteTeam(team) },
      ]);
      return;
    case 'plan-menu':
      e.stopPropagation();
      openMenu(el, [
        { label: 'Duplicate', run: () => duplicatePlan(team, plan) },
        ...(session.shared.has(plan)
          ? [{ label: 'Copy share link', run: () => copyShareLink(plan) }, { label: 'Stop sharing', run: () => void stopSharingPlan(plan) }]
          : [{ label: 'Share link', run: () => void openShareDialog(plan) }]),
        { label: 'Export gameplan', run: () => void showExport({ type: 'plan', teamId: team, planId: plan }) },
        '-',
        { label: 'Delete', cls: 'danger', run: () => void deletePlan(team, plan) },
      ]);
      return;
    case 'new-plan': e.stopPropagation(); return createPlan(team);
    case 'share-plan': return openShareDialog(plan);
    case 'replay-import': return openReplayImport();
    case 'copy-share': copyShareLink(plan); return;
    case 'stop-share': if (await stopSharingPlan(plan)) redrawSharedLinks(); return;
    case 'export-team': return showExport({ type: 'team', teamId: team });
    case 'delete-team': return deleteTeam(team);
    case 'export-plan': return showExport({ type: 'plan', teamId: team, planId: plan });
    case 'edit-paste': return editPaste(asSide(d.kind));
    case 'zoom': setZoom(store.ui.zoom + Number(d.d ?? 0) * config.zoom.step); return;
    case 'pick-slot': pickSlot(el, asSide(d.side), asSlot(d.kind), Number(d.i ?? 0)); return;
    case 'toggle-result': {
      e.stopPropagation();
      const id = d.node ?? '';
      store.ui.expanded[id] = !store.ui.expanded[id];
      rerenderCanvas();
      return;
    }
    case 'line-color': {
      const found = store.currentNode();
      if (!found) return;
      const color = (LINE_COLORS as readonly string[]).includes(d.color ?? '') ? (d.color as LineColor) : undefined;
      const name = found.node.line?.name ?? '';
      if (name || color) found.node.line = { name, ...(color ? { color } : {}) };
      else delete found.node.line;
      store.persist();
      rerenderCanvas();
      renderDrawer();
      return;
    }
    case 'remove-tag': {
      const found = store.currentNode();
      if (!found) return;
      found.node.tags = found.node.tags.filter((t) => t !== d.tag);
      store.persist();
      rerenderCanvas();
      renderDrawer();
      qs<HTMLInputElement>('#d-tag')?.focus();
      return;
    }
    case 'focus-line':
      store.ui.focusLine = d.node && d.node !== store.ui.focusLine ? d.node : null;
      rerenderCanvas();
      return;
    case 'select-node':
      store.ui.selectedNode = d.node ?? null;
      rerenderCanvas();
      renderDrawer();
      // The editor takes the canvas's right side: keep the clicked turn in view beside it.
      if (d.node) revealTurn(d.node);
      return;
    case 'close-drawer': {
      const was = store.ui.selectedNode;
      store.ui.selectedNode = null;
      qs('#drawer')?.classList.add('hidden');
      rerenderCanvas();
      // Phones: the editor covered the whole screen; come back to the turn that was being edited.
      if (was && isMobile()) revealTurn(was);
      return;
    }
    case 'select-tab':
      if (e.target instanceof HTMLInputElement) return; // clicking inside the rename box
      selectTab(d.tab ?? '');
      return;
    case 'add-tab': addTab(); return;
    case 'tab-menu': e.stopPropagation(); tabMenu(el, d.tab ?? ''); return;
    case 'add-node': addNode(d.parent || null); return;
    case 'copy-node': e.stopPropagation(); copyNode(d.node ?? ''); return;
    case 'add-branch-child': addNode(store.ui.selectedNode); return;
    case 'delete-node': return deleteNode();
  }
}

/** Phones: actions in the team menu that close it (opening something, or a dialog). Menus opened from it keep it open. */
const NAV_CLOSERS: ReadonlySet<string> = new Set(['open-team', 'open-plan', 'new-plan', 'new-team', 'import', 'export-all', 'theme', 'sign-in']);

type Field = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
const isField = (t: EventTarget | null): t is Field => t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t instanceof HTMLSelectElement;

/** Editor fields write straight into the store as you type (no full redraw, so focus stays). */
function onInput(e: Event): void {
  const t = e.target;
  if (!isField(t) || !t.closest('#drawer')) return;
  const found = store.currentNode();
  if (!found) return;
  const f = t.dataset.f;
  const a = t.dataset.a;
  if (f === 'line') {
    // A line needs a name or a colour; clearing both removes it.
    const name = t.value.trim();
    const color = found.node.line?.color;
    if (name || color) found.node.line = { name, ...(color ? { color } : {}) };
    else delete found.node.line;
  } else if (f === 'title' || f === 'note') {
    found.node[f] = t.value;
    if (f === 'title') { const h = qs('#drawer h3'); if (h) h.textContent = t.value || `Turn ${found.depth}`; }
  } else if (a) {
    const index = Number(t.dataset.i);
    const action = found.node.actions[index];
    if (!action) return;
    if (a === 'kind') action.kind = asKind(t.value);
    else if (a === 'mon' || a === 'target') action[a] = t.value;
    else if (a === 'pivot') { if (t.value) action.pivot = t.value; else delete action.pivot; }
    else if (a === 'move') {
      const wasPivot = isPivotMove(action.move);
      action.move = t.value;
      if (wasPivot !== isPivotMove(action.move)) {
        // Show or hide the "Then switch to" picker right away, keeping the caret in the move box.
        if (!isPivotMove(action.move)) delete action.pivot;
        const caret = t instanceof HTMLInputElement ? t.selectionStart : null;
        renderDrawer();
        const box = qs<HTMLInputElement>(`#a-move-${index}`);
        box?.focus();
        if (box && caret !== null) box.setSelectionRange(caret, caret);
      }
    } else return;
  } else if (t.dataset.tie) {
    // Speed tie: the chosen Pokémon moves ahead of the other one.
    const [a, b] = t.dataset.tie.split('|');
    const winner = t.value;
    const loser = winner === a ? b : a;
    if (!winner || !loser) return;
    const node = found.node;
    node.tieOrder = [...(node.tieOrder ?? []).filter((k) => k !== winner && k !== loser), winner, loser];
    // Both tied Pokémon show this pick: redraw the editor so they agree (and "(assumed)" goes away).
    store.persist();
    rerenderCanvas();
    renderDrawer();
    return;
  } else if (t.dataset.o) {
    const action = found.node.actions[Number(t.dataset.i)];
    if (!action) return;
    setOutcome(action, t.dataset.o, t.dataset.t ?? '', t);
  } else return;
  store.persist();
  rerenderCanvas();
}

/** Some edits change what the editor itself should offer (move suggestions, switch target list). */
function onChange(e: Event): void {
  const t = e.target;
  if (t instanceof HTMLInputElement && t.id === 'imp-file' && t.files?.[0]) { loadImportFile(t.files[0]); return; }
  if (!isField(t) || !t.closest('#drawer')) return;
  const a = t.dataset.a;
  const found = store.currentNode();
  const action = found?.node.actions[Number(t.dataset.i)];
  if (!found || !action) return;
  // A switch target changes who is left to pivot into.
  if (a !== 'kind' && a !== 'mon' && !(a === 'target' && action.kind === 'switch')) return;
  if (a === 'kind') { if (action.kind === 'switch') action.move = ''; action.target = ''; delete action.pivot; }
  store.persist();
  renderDrawer();
  rerenderCanvas();
}

/** Adds what's typed in the tag box as a tag of the open turn. */
function addTypedTag(box: HTMLInputElement): void {
  const found = store.currentNode();
  const value = box.value.replace(/,/g, ' ').trim();
  if (!found || !value) return;
  found.node.tags = cleanTags([...found.node.tags, value]);
  store.persist();
  rerenderCanvas();
  renderDrawer();
  qs<HTMLInputElement>('#d-tag')?.focus();
}

function onKeydown(e: KeyboardEvent): void {
  const t = e.target;
  if (t instanceof HTMLInputElement && t.id === 'd-tag') {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTypedTag(t); return; }
    // Backspace in an empty box takes off the last tag, as in most tag inputs.
    if (e.key === 'Backspace' && !t.value) {
      const found = store.currentNode();
      if (found?.node.tags.length) { found.node.tags = found.node.tags.slice(0, -1); store.persist(); rerenderCanvas(); renderDrawer(); qs<HTMLInputElement>('#d-tag')?.focus(); }
      return;
    }
  }
  if ((e.key === 'Enter' || e.key === ' ') && t instanceof HTMLElement && t.matches('[role="button"][data-act], [role="tab"][data-act]')) {
    e.preventDefault();
    t.click();
  }
  // F2 renames the focused tab, as in spreadsheets.
  if (e.key === 'F2' && t instanceof HTMLElement && t.matches('.sheet-tab[data-tab]')) {
    e.preventDefault();
    startRenameTab(t.dataset.tab ?? '');
  }
  if (e.key === 'Escape' && store.ui.selectedNode && !qs('.overlay')) {
    store.ui.selectedNode = null;
    qs('#drawer')?.classList.add('hidden');
    rerenderCanvas();
  }
}

export function installEvents(): void {
  document.addEventListener('click', (e) => {
    const el = e.target instanceof Element ? e.target.closest<HTMLElement>('[data-act]') : null;
    if (el) handleClick(el, e).catch(reportError);
  });
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('dblclick', (e) => {
    const tab = e.target instanceof Element ? e.target.closest<HTMLElement>('.sheet-tab[data-tab]') : null;
    if (tab && !(e.target instanceof HTMLInputElement)) startRenameTab(tab.dataset.tab ?? '');
  });
  document.addEventListener('input', onInput);
  document.addEventListener('change', onChange);
  // A suggestion picked from the tag list arrives as a plain input event with the whole tag: add it.
  document.addEventListener('input', (e) => {
    const t = e.target;
    if (t instanceof HTMLInputElement && t.id === 'd-tag' && (e as InputEvent).inputType === 'insertReplacementText') addTypedTag(t);
  });
  simService.onChange = () => rerenderCanvas();
}
